import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ClipboardList, Layers, Users, ArrowRight, CheckCircle } from 'lucide-react';
import { isLondonBookFairActive, isSummerWelcomeActive } from '../utils/dateUtils';
import LbfAppDemoButton from './LbfAppDemoButton';

const ProgressDonut = ({ progress, size = 24, strokeWidth = 2, color = "green" }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
        <div className="relative" style={{ width: size, height: size }}>
            {/* Background circle */}
            <svg className="absolute" width={size} height={size}>
                <circle
                    className="text-gray-200"
                    strokeWidth={strokeWidth}
                    stroke="currentColor"
                    fill="transparent"
                    r={radius}
                    cx={size / 2}
                    cy={size / 2}
                />
            </svg>
            {/* Progress circle */}
            <svg className="absolute transform -rotate-90" width={size} height={size}>
                <circle
                    className={`text-${color}-600`}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r={radius}
                    cx={size / 2}
                    cy={size / 2}
                />
            </svg>
        </div>
    );
};

const ProgressCard = ({ title, description, progress, icon: Icon, buttonText, onClick }) => {
    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-green-50 rounded-lg">
                            <Icon className="w-6 h-6 text-green-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-600 mb-4">{description}</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="text-2xl font-bold text-green-600">{progress}%</div>
                    <div className="text-sm text-gray-500">complete</div>
                </div>
            </div>
            
            {/* Progress bar */}
            <div className="w-full h-2 bg-gray-100 rounded-full mb-4">
                <div 
                    className="h-full bg-green-600 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                />
            </div>

            <button
                onClick={onClick}
                className="w-full mt-4 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
                {buttonText}
                <ArrowRight className="ml-2 w-4 h-4" />
            </button>
        </div>
    );
};

const TeamMemberCard = ({ member, organization }) => {
    const [progress, setProgress] = useState({ questionnaire: 0, features: 0 });
    const [lastActive, setLastActive] = useState(null);

    useEffect(() => {
        const fetchMemberProgress = async () => {
            try {
                // Import authenticatedFetch for proper session handling
                const { authenticatedFetch } = await import('../utils/apiUtils');
                
                // Fetch all necessary data in parallel using authenticatedFetch
                const [
                    questionsResponse, 
                    answersResponse, 
                    featuresResponse, 
                    responsesResponse,
                    commentsResponse
                ] = await Promise.all([
                    authenticatedFetch('/api/core-questions'),
                    authenticatedFetch(`/api/core-questions/responses/${organization.id}/team`),
                    authenticatedFetch(`/api/features/${organization.id}`),
                    authenticatedFetch(`/api/responses/${organization.id}`),
                    authenticatedFetch(`/api/comments/${organization.id}`)
                ]);

                if (questionsResponse.ok && answersResponse.ok && featuresResponse.ok && 
                    responsesResponse.ok && commentsResponse.ok) {
                    const [questions, answers, features, responses, comments] = await Promise.all([
                        questionsResponse.json(),
                        answersResponse.json(),
                        featuresResponse.json(),
                        responsesResponse.json(),
                        commentsResponse.json()
                    ]);
                    
                    // Calculate questionnaire progress
                    const memberAnswers = answers.filter(a => 
                        a.user_id === member.id && 
                        a.response_value !== null && 
                        a.response_value !== undefined
                    );
                    const questionnairePercent = Math.round((memberAnswers.length / questions.length) * 100) || 0;

                    // Calculate feature progress
                    const memberResponses = responses.filter(r => 
                        r.user_id === member.id && 
                        r.response_value !== null
                    );
                    const featurePercent = Math.round((memberResponses.length / features.length) * 100) || 0;

                    // Calculate last active time from both responses and comments
                    const userResponses = responses.filter(r => r.user_id === member.id);
                    const userComments = Object.values(comments)
                        .flat()
                        .filter(c => c.user_id === member.id);

                    const responseDates = userResponses.map(r => new Date(r.updated_at || r.created_at));
                    const commentDates = userComments.map(c => new Date(c.created_at));
                    const allDates = [...responseDates, ...commentDates].filter(date => !isNaN(date));

                    if (allDates.length > 0) {
                        const mostRecent = new Date(Math.max(...allDates));
                        setLastActive(mostRecent.toISOString());
                    }

                    setProgress({
                        questionnaire: questionnairePercent,
                        features: featurePercent
                    });
                }
            } catch (error) {
                console.error('Error fetching member progress:', error);
            }
        };

        fetchMemberProgress();
    }, [member.id, organization.id]);

    // Format the last active time
    const formatLastActive = (timestamp) => {
        if (!timestamp) return 'Never';
        
        const date = new Date(timestamp);
        const now = new Date();
        const diffInHours = Math.abs(now - date) / 36e5; // Convert to hours

        if (diffInHours < 24) {
            // If less than 24 hours ago, show relative time
            if (diffInHours < 1) {
                const minutes = Math.round(diffInHours * 60);
                return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
            }
            return `${Math.round(diffInHours)} hour${diffInHours !== 1 ? 's' : ''} ago`;
        } else if (diffInHours < 48) {
            return 'Yesterday';
        } else {
            return date.toLocaleDateString();
        }
    };

    return (
        <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
            {/* User Avatar */}
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <span className="text-sm font-medium text-green-700">
                    {member.name.split(' ').map(n => n[0]).join('')}
                </span>
            </div>

            {/* User Info - 30% width */}
            <div className="flex-1 min-w-[30%] max-w-[30%]">
                <div className="font-medium text-gray-900">{member.name}</div>
                <div className="text-sm text-gray-500 mt-1">{member.email}</div>
            </div>

            {/* Status and Role - 15% width */}
            <div className="flex-shrink-0 w-[15%]">
                <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full
                    ${member.status === 'active' ? 'bg-green-100 text-green-800' :
                      member.status === 'suspended' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'}`}>
                    {member.status || 'Active'}
                </span>
                <div className="text-sm text-gray-500 mt-1">{member.role || 'Member'}</div>
            </div>

            {/* Progress Indicators - 35% width */}
            <div className="flex items-center justify-center gap-8 w-[35%]">
                <div className="flex flex-col items-center">
                    <ProgressDonut progress={progress.questionnaire} color="blue" />
                    <span className="text-xs text-gray-500 mt-1">Questionnaire</span>
                </div>
                <div className="flex flex-col items-center">
                    <ProgressDonut progress={progress.features} color="green" />
                    <span className="text-xs text-gray-500 mt-1">Features</span>
                </div>
            </div>

            {/* Last Active - 20% width */}
            <div className="flex-shrink-0 w-[20%] text-right">
                <span className="text-xs font-medium text-gray-700">Last Active</span>
                <div className="text-sm text-gray-500">{formatLastActive(lastActive)}</div>
            </div>
        </div>
    );
};

const Dashboard = ({ 
    organization,
    teamMembers = [],
    questionnaireProgress = 0,
    featureProgress = 0,
    user
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showLBF, setShowLBF] = useState(false);
    const [showSummerWelcome, setShowSummerWelcome] = useState(false);
    
    // Check for active promotional periods
    useEffect(() => {
        const lbfActive = isLondonBookFairActive();
        const summerActive = isSummerWelcomeActive();
        
        setShowLBF(lbfActive);
        setShowSummerWelcome(summerActive);
    }, []);
    
    // Redirect to appropriate promotional dashboard
    useEffect(() => {
        // Check if user explicitly wants to access dashboard (via URL param or navigation state)
        const forceDashboard = new URLSearchParams(location.search).get('force') === 'true' || 
                              location.state?.forceDashboard === true;
        
        console.log('Dashboard: Checking redirect logic', { 
            showLBF, 
            showSummerWelcome, 
            forceDashboard,
            searchParams: location.search,
            state: location.state
        });
        
        // Only redirect if not explicitly requesting dashboard
        if (!forceDashboard) {
        // Priority: LBF first, then Summer Welcome, then regular dashboard
        if (showLBF) {
                console.log('Dashboard: Redirecting to LBF');
            navigate('/lbf');
        } else if (showSummerWelcome) {
                console.log('Dashboard: Redirecting to Summer Welcome');
            navigate('/summer-welcome');
            }
        } else {
            console.log('Dashboard: Force dashboard access, staying on dashboard');
        }
    }, [showLBF, showSummerWelcome, navigate, location]);
    
    // Original dashboard content
    return (
        <div className="container mx-auto px-4 py-8">
            {/* London Book Fair App Demo Button */}
            <LbfAppDemoButton onAppDemoClick={() => navigate('/app-wizard')} />
            
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">Welcome to {organization?.name || 'Your Dashboard'}</h1>
                <p className="text-gray-600 mt-1">Track your progress and collaborate with your team</p>
            </div>

            {/* Progress Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <ProgressCard 
                    title="Requirements Questionnaire" 
                    description="Complete the core questionnaire to help us understand your requirements"
                    progress={questionnaireProgress}
                    icon={ClipboardList}
                    buttonText="Continue Questionnaire"
                    onClick={() => navigate('/questionnaire')}
                />
                
                <ProgressCard 
                    title="Feature Selection" 
                    description="Select the features you need for your project"
                    progress={featureProgress}
                    icon={Layers}
                    buttonText="Select Features"
                    onClick={() => navigate('/requirements')}
                />
            </div>

            {/* Team Section */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center">
                        <Users className="w-5 h-5 text-gray-500 mr-2" />
                        <h2 className="text-lg font-medium text-gray-900">Your Team</h2>
                    </div>
                    <span className="text-sm text-gray-500">{teamMembers.length} members</span>
                </div>
                
                {teamMembers.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {teamMembers.map(member => (
                            <TeamMemberCard 
                                key={member.id} 
                                member={member} 
                                organization={organization}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-500">No team members yet</p>
                    </div>
                )}
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Links</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <a 
                        href="/questionnaire" 
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center">
                            <div className="p-2 bg-green-50 rounded-lg mr-3">
                                <ClipboardList className="w-5 h-5 text-green-600" />
                            </div>
                            <span className="font-medium">Requirements Questionnaire</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400" />
                    </a>
                    
                    <a 
                        href="/requirements" 
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center">
                            <div className="p-2 bg-green-50 rounded-lg mr-3">
                                <Layers className="w-5 h-5 text-green-600" />
                            </div>
                            <span className="font-medium">Feature Selection</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400" />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Dashboard; 